import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { propOr, uniq } from 'ramda';

import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import { directions } from 'constants/booking';
import { Page, Row } from 'components/shared/Layout';
import AlertBox from 'components/ui/AlertBox';

import AfterSalesCtas from './AfterSalesCtas';
import AfterSalesWarning from './AfterSalesWarning';
import HotelStatusConflictWarning from '../HotelStatusConflictWarning';
import MultiInventoryConflictWarning from './MultiInventoryConflictWarning';
import JourneyLegs from './JourneyLegs';
import { StyledRow } from './Journeys.styles';
import SelectPassengers from './SelectPassengers';
import { isSegmentASummatedFare } from 'utils/summatedFares';
import OverpaidWarningPopup from '../OverpaidWarningPopup';
import { bookingStatus } from '../../../types/booking';
import { useLazyQuery } from '@apollo/client';
import { GET_BOOKING_ITEMS } from 'store/queries';
import { POINTS_REDEMPTION_MISMATCH_KEY } from 'constants/clubEurostar';
import PointsMismatchWarning from './AfterSalesWarning/PointsMismatchWarning/PointsMismatchWarning';
import UnderpaidWarningPopup from '../UnderpaidWarningPopup';
import { legsAndProductsWithoutDuplicates } from 'utils/legsAndProductsWithoutDuplicates';
import MissingInfantUuidModal from '../MissingInfantUuidModal';
import { canUserCloneExternalTickets } from './AfterSalesCtas/CloneExternalTicketsCta/CloneExternalTicketsCta.utils';

const { OVERPAID, PENDING_CHANGES } = bookingStatus;

const Journeys = ({
  booking: { reference, currency, passengers, latestRevisionSource, isInAfterSales },
  booking,
  user,
  selectedItems,
  isGroupChecked,
  toggleGroup,
  refetchBooking,
  ticketDetails,
  history
}) => {
  const [getBookingItems, { data: bookingItemsData, called: getBookingItemsCalled }] = useLazyQuery(GET_BOOKING_ITEMS);
  const bookingItems = bookingItemsData?.getBookingItems;
  const isOutboundSummatedFare = isSegmentASummatedFare(booking.outbound);
  const isInboundSummatedFare = isSegmentASummatedFare(booking.inbound);
  const [missedDepartureNoTrainAvailable, setMissedDepartureNoTrainAvailable] = useState(false);
  const canSelectExternalTicketsIndependently = canUserCloneExternalTickets(user);

  const [shouldDisplayPointsRedemptionMismatch, setShouldDisplayPointsRedemptionMismatch] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(POINTS_REDEMPTION_MISMATCH_KEY)) {
      setShouldDisplayPointsRedemptionMismatch(true);
    }
  }, [setShouldDisplayPointsRedemptionMismatch]);

  const onPointsRedemptionMismatchClose = (shouldRedirect) => {
    const mismatchContext = JSON.parse(localStorage.getItem(POINTS_REDEMPTION_MISMATCH_KEY));

    localStorage.removeItem(POINTS_REDEMPTION_MISMATCH_KEY);
    setShouldDisplayPointsRedemptionMismatch(false);

    if (shouldRedirect) {
      history.push(`/clubeurostar/${mismatchContext.membershipNumber}#points-statement`);
    }
  };

  const isOverpaid = booking.displayStatus === OVERPAID;
  const isBookingUnderpaid = booking.status === bookingStatus.UNDERPAID;
  const isBookingMultiJourney = !!booking.additionalDetails.find((ad) => ad.key === 'multi_journey' && ad.value === 'true');
  let multiJourneyLegs = [];

  let journeys = booking.additionalDetails.filter((ad) => ad.key.includes('segment_') && ad.value.includes('journey_'));

  for (let journeyDetail of journeys) {
    let journeyId = journeyDetail.value.split('_')[1];
    let journey = multiJourneyLegs.find((l) => l.journeyId === journeyId);

    if (!journey) {
      journey = {
        journeyId: journeyId,
        inbound: [],
        outbound: []
      };

      multiJourneyLegs.push(journey);
    }

    journey.inbound.push(...booking.inbound.filter((l) => l.id === journeyDetail.key));
    journey.outbound.push(...booking.outbound.filter((l) => l.id === journeyDetail.key));
  }

  const trainsTariffCodes = uniq(
    [...booking.inbound, ...booking.outbound]
      ?.map((leg) => leg.products)
      ?.flat()
      ?.map((product) => product?.tariffDetails?.tariffCode)
  );

  useEffect(() => {
    if (!trainsTariffCodes.length || getBookingItemsCalled) {
      return;
    }
    getBookingItems({
      variables: {
        bookingReference: booking.reference,
        tariffCodes: trainsTariffCodes
      }
    });
  }, [getBookingItems, trainsTariffCodes, booking, getBookingItemsCalled]);

  return (
    <>
      <AfterSalesWarning
        isInAfterSales={isInAfterSales || [PENDING_CHANGES].includes(booking.status)}
        latestRevisionAgent={propOr('', 'username', latestRevisionSource)}
        reference={reference}
        booking={booking}
      />
      {isBookingUnderpaid && <UnderpaidWarningPopup booking={booking} />}
      <MissingInfantUuidModal booking={booking} />
      <OverpaidWarningPopup booking={booking} isOverpaid={isOverpaid} />
      {!isInAfterSales && ![PENDING_CHANGES].includes(booking.status) && (
        <HotelStatusConflictWarning booking={booking} refetch={refetchBooking} />
      )}

      {shouldDisplayPointsRedemptionMismatch && (
        <PointsMismatchWarning isOpen={shouldDisplayPointsRedemptionMismatch} closeModal={onPointsRedemptionMismatchClose} />
      )}

      <MultiInventoryConflictWarning booking={booking} />
      <Page>
        {missedDepartureNoTrainAvailable && (
          <Row span={12} start={2}>
            <AlertBox variant={AlertBox.variants.WARNING}>
              <FormattedMessage id="afterSales.missedDeparture.noTrainAvailable" />
            </AlertBox>
          </Row>
        )}
        <Row id="booking-journeys" span={13} start={1}>
          {isBookingMultiJourney &&
            multiJourneyLegs
              .filter((l) => l.inbound.length > 0 || l.outbound.length > 0)
              .map((l, index) => (
                <React.Fragment key={index}>
                  <h1>
                    <FormattedMessage id="multiJourney.journeyLabelId" values={{ journeyId: index + 1 }} />
                  </h1>

                  {directions.map((direction) => (
                    <JourneyLegs
                      bookingItems={bookingItems}
                      id={direction}
                      key={direction}
                      legs={l[direction]}
                      legsWithExchangedProducts={booking[`${direction}WithExchangedProducts`]}
                      booking={booking}
                      direction={direction}
                      passengers={passengers}
                      isGroupChecked={isGroupChecked}
                      toggleGroup={toggleGroup}
                      currency={currency}
                      reference={reference}
                      isOutboundSummatedFare={isOutboundSummatedFare}
                      isInboundSummatedFare={isInboundSummatedFare}
                      canSelectExternalTicketsIndependently={canSelectExternalTicketsIndependently}
                    />
                  ))}
                </React.Fragment>
              ))}
          {!isBookingMultiJourney &&
            directions.map((direction) => {
              const showCancelledLegs = booking.isGroupBooking && !booking[direction].length;
              const directionLegs = showCancelledLegs ? booking[`${direction}Cancelled`] : booking[direction];

              return (
                <JourneyLegs
                  bookingItems={bookingItems}
                  id={direction}
                  key={direction}
                  legs={legsAndProductsWithoutDuplicates({ legs: directionLegs })}
                  legsWithExchangedProducts={booking[`${direction}WithExchangedProducts`]}
                  booking={booking}
                  direction={direction}
                  passengers={passengers}
                  isGroupChecked={isGroupChecked}
                  toggleGroup={toggleGroup}
                  currency={currency}
                  reference={reference}
                  isOutboundSummatedFare={isOutboundSummatedFare}
                  isInboundSummatedFare={isInboundSummatedFare}
                  canSelectExternalTicketsIndependently={canSelectExternalTicketsIndependently}
                  ticketDetails={ticketDetails}
                />
              );
            })}
        </Row>
        <StyledRow span={3} start={14}>
          <SelectPassengers booking={booking} selectedItems={selectedItems} toggleGroup={toggleGroup} />
          <AfterSalesCtas
            user={user}
            booking={booking}
            bookingReference={reference}
            selectedItemRefs={selectedItems}
            isOutboundSummatedFare={isOutboundSummatedFare}
            isInboundSummatedFare={isInboundSummatedFare}
            setMissedDepartureNoTrainAvailable={setMissedDepartureNoTrainAvailable}
            history={history}
            refetchBooking={refetchBooking}
          />
        </StyledRow>
      </Page>
    </>
  );
};

Journeys.propTypes = {
  booking: bookingShape.isRequired,
  user: userShape.isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.string),
  isGroupChecked: PropTypes.func,
  toggleGroup: PropTypes.func,
  refetchBooking: PropTypes.func.isRequired,
  ticketDetails: PropTypes.arrayOf(PropTypes.object),
  history: historyShape
};

export default Journeys;
